import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Star, MapPin, BadgeCheck, Calendar, MessageCircle, Award, Briefcase,
  Languages, Clock4, Check, ChevronRight, Crown, Loader2,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { REVIEWS } from "../lib/mockData";
import { api } from "../lib/api";

const DATES = [
  { d: "Mon", n: 8, slots: 3 }, { d: "Tue", n: 9, slots: 5 }, { d: "Wed", n: 10, slots: 0 },
  { d: "Thu", n: 11, slots: 4 }, { d: "Fri", n: 12, slots: 2 }, { d: "Sat", n: 13, slots: 6 }, { d: "Mon", n: 15, slots: 4 },
];
const SLOTS = ["10:30 AM", "11:30 AM", "2:00 PM", "3:30 PM", "5:00 PM"];

export default function AttorneyProfile() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(1);
  const [selectedSlot, setSelectedSlot] = useState(2);

  useEffect(() => {
    (async () => {
      try { setP(await api.getAttorney(id)); }
      catch { setP(null); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="surface-cream min-h-screen">
        <Header/>
        <div className="max-w-[1400px] mx-auto px-5 py-24 text-center text-[#5A6B66]"><Loader2 className="inline animate-spin mr-2"/> Loading attorney…</div>
        <Footer/>
      </div>
    );
  }
  if (!p) {
    return (
      <div className="surface-cream min-h-screen">
        <Header/>
        <div className="max-w-[1400px] mx-auto px-5 py-24 text-center">
          <h1 className="font-display text-3xl font-bold text-[#0A2520] mb-3">Attorney not found</h1>
          <Link to="/attorneys" className="btn-primary">Back to directory</Link>
        </div>
        <Footer/>
      </div>
    );
  }
  const name = p.firm_name || p.name;
  const patentsFiled = p.patents_filed || 0;
  const priceFrom = p.price_from || 0;
  const successRate = p.success_rate || 95;

  return (
    <div className="surface-cream min-h-screen" data-testid="profile-page">
      <Header />

      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pt-5 pb-3 text-xs text-[#5A6B66] flex items-center gap-1">
        <Link to="/" className="hover:text-[#0E6E5E]">Home</Link>
        <ChevronRight size={12}/>
        <Link to="/attorneys" className="hover:text-[#0E6E5E]">Attorneys</Link>
        <ChevronRight size={12}/>
        <span className="text-[#0A2520] font-medium">{name}</span>
      </div>

      {/* Profile header */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pt-4 pb-8">
        <div className="card p-6 md:p-8 grid md:grid-cols-[auto_1fr_auto] gap-6 items-start" data-testid="profile-header">
          <img src={p.avatar} alt={name} className="w-28 h-28 rounded-2xl object-cover"/>
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0A2520]">{name}</h1>
              {p.premium && <span className="chip-gold chip"><Crown size={11}/> Premium</span>}
              <span className="chip inline-flex items-center gap-1 bg-[#0E6E5E] text-white"><BadgeCheck size={12}/> Verified</span>
            </div>
            <p className="text-[#5A6B66] mb-3">{p.tagline} · <MapPin size={12} className="inline -mt-0.5"/> {p.location || p.city}</p>
            <div className="flex flex-wrap items-center gap-4 text-sm mb-4">
              <div className="flex items-center gap-1.5">
                <Star size={14} className="fill-[#7CC242] text-[#7CC242]"/>
                <span className="font-bold text-[#0A2520]">{p.rating || "—"}</span>
                <span className="text-[#5A6B66]">({p.reviews || 0} reviews)</span>
              </div>
              <div className="flex items-center gap-1.5 text-[#3D4F4A]"><Briefcase size={14}/> {patentsFiled.toLocaleString()} patents</div>
              <div className="flex items-center gap-1.5 text-[#3D4F4A]"><Clock4 size={14}/> {p.turnaround || "9-12 days"}</div>
              <div className="flex items-center gap-1.5 text-[#3D4F4A]"><Languages size={14}/> {(p.languages || []).join(", ")}</div>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {(p.specializations || []).map((s) => <span key={s} className="chip">{s}</span>)}
            </div>
          </div>
          <div className="md:text-right flex md:flex-col items-start md:items-end gap-3">
            <div>
              <div className="text-xs text-[#5A6B66]">Starting from</div>
              <div className="font-display text-4xl font-extrabold text-[#0A2520]">₹{priceFrom.toLocaleString('en-IN')}</div>
              <div className="text-xs text-[#5A6B66]">Per patent application · all-inclusive</div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-16 grid lg:grid-cols-[1fr_380px] gap-8">
        <main className="space-y-6">
          <section className="card p-7">
            <h2 className="font-display font-bold text-xl text-[#0A2520] mb-3">About the firm</h2>
            <p className="text-[#3D4F4A] leading-relaxed mb-5">{p.bio}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-5 border-t border-[#F0F4F0]">
              <Stat label="Patents filed" value={patentsFiled.toLocaleString()}/>
              <Stat label="Success rate" value={`${successRate}%`}/>
              <Stat label="Avg. response" value={p.response || "< 2 hrs"}/>
              <Stat label="Years in IP" value="14+"/>
            </div>
          </section>

          <section className="card p-7">
            <h2 className="font-display font-bold text-xl text-[#0A2520] mb-5">Senior team</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { name: "Sanjay Verma", role: "Managing Partner · Pharma & Life Sciences" },
                { name: "Aakanksha Singh", role: "Partner · Software & AI" },
                { name: "Karan Bhatia", role: "Senior Associate · PCT Filings" },
              ].map((t) => (
                <div key={t.name} className="text-center">
                  <img src={p.avatar} alt={t.name} className="w-16 h-16 rounded-2xl object-cover mx-auto mb-2"/>
                  <div className="font-display font-semibold text-sm text-[#0A2520]">{t.name}</div>
                  <div className="text-xs text-[#5A6B66]">{t.role}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="card p-7">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-bold text-xl text-[#0A2520]">Featured patents</h2>
              <span className="text-xs text-[#5A6B66]">Last 12 months</span>
            </div>
            <div className="space-y-3">
              {[
                { title: "Novel mRNA stabiliser for tropical-climate vaccine logistics", type: "Utility · Pharma", year: 2025, status: "Granted" },
                { title: "Edge-ML inference accelerator with sub-3W power envelope", type: "Utility · Semiconductor", year: 2025, status: "Granted" },
                { title: "Solar-coupled cold-storage container for MSME exporters", type: "Design + Utility", year: 2024, status: "Granted" },
                { title: "Topology-aware federated learning for clinical data", type: "Software · AI", year: 2024, status: "Published" },
              ].map((w) => (
                <div key={w.title} className="flex items-start gap-4 p-4 rounded-xl surface-sage hover:bg-[#DFEAE0] transition cursor-pointer">
                  <Award className="text-[#0E6E5E] flex-shrink-0 mt-1" size={18}/>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-[#0A2520] mb-1 truncate">{w.title}</div>
                    <div className="text-xs text-[#5A6B66]">{w.type} · {w.year}</div>
                  </div>
                  <span className={`chip ${w.status === "Granted" ? "bg-[#0E6E5E] text-white" : ""}`}>{w.status}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="card p-7">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-bold text-xl text-[#0A2520]">Reviews ({p.reviews || 0})</h2>
              <div className="flex items-center gap-1.5">
                <Star size={14} className="fill-[#7CC242] text-[#7CC242]"/>
                <span className="font-bold">{p.rating || "—"}</span>
                <span className="text-[#5A6B66] text-sm">overall</span>
              </div>
            </div>
            <div className="space-y-5">
              {REVIEWS.map((r) => (
                <div key={r.name} className="pb-5 border-b border-[#F0F4F0] last:border-0 last:pb-0">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div>
                      <div className="font-display font-semibold text-[#0A2520]">{r.name}</div>
                      <div className="text-xs text-[#5A6B66]">{r.role} · {r.date}</div>
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => <Star key={i} size={12} className={i < r.rating ? "fill-[#7CC242] text-[#7CC242]" : "text-[#E1E8E2]"}/>)}
                    </div>
                  </div>
                  <p className="text-sm text-[#3D4F4A] leading-relaxed">{r.body}</p>
                </div>
              ))}
            </div>
          </section>
        </main>

        <aside className="lg:sticky lg:top-[90px] h-fit space-y-4">
          <div className="card p-6" data-testid="booking">
            <div className="font-display font-bold text-lg text-[#0A2520] mb-1">Schedule a consultation</div>
            <p className="text-xs text-[#5A6B66] mb-5">30-min free intro · in English/Hindi/Marathi</p>

            <div className="mb-5">
              <div className="text-xs font-bold text-[#0A2520] uppercase tracking-wider mb-3">Pick a date</div>
              <div className="grid grid-cols-7 gap-1.5">
                {DATES.map((d, i) => (
                  <button key={i} disabled={d.slots === 0} onClick={() => setSelectedDate(i)}
                    className={`p-2 rounded-lg text-center transition disabled:opacity-30 disabled:cursor-not-allowed ${selectedDate === i ? "bg-[#0E6E5E] text-white" : "hover:surface-sage"}`}
                    data-testid={`date-${i}`}>
                    <div className="text-[10px] uppercase tracking-wider opacity-80">{d.d}</div>
                    <div className="text-base font-bold">{d.n}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <div className="text-xs font-bold text-[#0A2520] uppercase tracking-wider mb-3">Available slots</div>
              <div className="grid grid-cols-2 gap-2">
                {SLOTS.map((s, i) => (
                  <button key={s} onClick={() => setSelectedSlot(i)}
                    className={`py-2.5 rounded-lg text-sm font-semibold transition ${selectedSlot === i ? "bg-[#0E6E5E] text-white" : "border border-[#E1E8E2] text-[#3D4F4A] hover:border-[#0E6E5E]"}`}
                    data-testid={`slot-${i}`}>{s}</button>
                ))}
              </div>
            </div>

            <button className="btn-primary w-full justify-center mb-3" data-testid="confirm-booking"><Calendar size={16}/> Confirm appointment</button>
            <button className="btn-outline w-full justify-center text-sm" data-testid="message-btn"><MessageCircle size={14}/> Send a message first</button>

            <div className="mt-5 pt-5 border-t border-[#F0F4F0] space-y-2 text-xs text-[#5A6B66]">
              <Item icon={Check}>30-min intro call is always free</Item>
              <Item icon={Check}>NDA signed before any document share</Item>
              <Item icon={Check}>Refund-protected via Udhyak escrow</Item>
            </div>
          </div>

          <div className="card p-5 bg-gradient-to-br from-[#EBF3EC] to-white border-[#7CC242]/30">
            <div className="font-display font-semibold text-sm text-[#0B5447] mb-1">Quote for your brief</div>
            <p className="text-xs text-[#3D4F4A] mb-3">"AI-powered crop disease detection" · Utility · Software & AI</p>
            <div className="font-display text-3xl font-extrabold text-[#0E6E5E] mb-1">₹{priceFrom.toLocaleString('en-IN')}</div>
            <p className="text-xs text-[#3D4F4A]">Fixed quote · valid 7 days · escrow protected</p>
          </div>
        </aside>
      </div>

      <Footer/>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-2xl font-extrabold text-[#0E6E5E]">{value}</div>
      <div className="text-xs text-[#5A6B66] mt-0.5">{label}</div>
    </div>
  );
}

function Item({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={12} className="text-[#0E6E5E] flex-shrink-0 mt-0.5"/>
      <span>{children}</span>
    </div>
  );
}
